import type {
  Brand,
  BrandCreate,
  BrandExtraction,
  BrandSummary,
  BrandUpdate,
  DataResponse,
  Members,
  MembershipRole,
  PaginatedResponse,
  TeamMemberGrant,
  UserMember,
} from "@/types";
import { api } from "../api";

const PREFIX = "/brands";

export const brandService = {
  /** List brands the caller can generate with (includes the company default). */
  list: (page = 1, pageSize = 100) =>
    api.get<PaginatedResponse<BrandSummary>>(`${PREFIX}?page=${page}&pageSize=${pageSize}`),

  get: (uuid: string) => api.get<DataResponse<Brand>>(`${PREFIX}/${uuid}`),

  create: (data: BrandCreate) => api.post<DataResponse<Brand>>(PREFIX, data),

  update: (uuid: string, data: BrandUpdate) => api.put<DataResponse<Brand>>(`${PREFIX}/${uuid}`, data),

  delete: (uuid: string) => api.delete<{ message: string }>(`${PREFIX}/${uuid}`),

  /** Upload/replace a brand's logo (multipart, field name "file"). */
  uploadLogo: (uuid: string, file: File) =>
    api.uploadBlob<DataResponse<Brand>>(`${PREFIX}/${uuid}/logo`, file, file.name, "file"),

  /**
   * Extract a draft brand kit + logo candidates from a guidelines document
   * (PDF/DOCX/PPTX). Non-persisting — the result pre-fills the form for review.
   */
  extract: (file: File) => api.uploadBlob<DataResponse<BrandExtraction>>(`${PREFIX}/extract`, file, file.name, "file"),
};

/** Membership (access) management — satisfies the shared MembershipService contract. */
export const brandMembershipService = {
  getMembers: (uuid: string) => api.get<DataResponse<Members>>(`${PREFIX}/${uuid}/members`),

  addUserMember: (uuid: string, userUuid: string, role: MembershipRole) =>
    api.post<DataResponse<UserMember>>(`${PREFIX}/${uuid}/members/users`, { userUuid, role }),
  updateUserMemberRole: (uuid: string, userUuid: string, role: MembershipRole) =>
    api.patch<DataResponse<UserMember>>(`${PREFIX}/${uuid}/members/users/${userUuid}`, { role }),
  removeUserMember: (uuid: string, userUuid: string) => api.delete(`${PREFIX}/${uuid}/members/users/${userUuid}`),

  addTeamMember: (uuid: string, teamUuid: string, role: MembershipRole) =>
    api.post<DataResponse<TeamMemberGrant>>(`${PREFIX}/${uuid}/members/teams`, { teamUuid, role }),
  updateTeamMemberRole: (uuid: string, teamUuid: string, role: MembershipRole) =>
    api.patch<DataResponse<TeamMemberGrant>>(`${PREFIX}/${uuid}/members/teams/${teamUuid}`, { role }),
  removeTeamMember: (uuid: string, teamUuid: string) => api.delete(`${PREFIX}/${uuid}/members/teams/${teamUuid}`),
};
